import React from 'react';
import { Users, Users2, Globe, Clock, Building2, Briefcase, HardHat, X } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ReferenceLine, PieChart, Pie, Cell
} from 'recharts';
import Modal from '../UI/Modal';
import {
  CHART_COLORS_6, CHART_COLORS_5, CHART_AXIS_COLOR, CHART_GRID_COLOR,
  CHART_TOOLTIP_STYLE, AGE_GROUP_COLORS, TENURE_COLORS
} from '../../config/dashboardConstants';
import { getDepartmentDashboardColor, getDepartmentIcon } from '../../config/departmentConfig';

const WorkforceAnalyticsSection = ({
  ageGroupData,
  workforceAgeProfile,
  nationalityData,
  raceData,
  genderData,
  tenureData,
  departmentData,
  showDepartmentModal,
  setShowDepartmentModal,
  stats,
}) => {
  return (
    <>
      {/* Analytics and Trends Section */}
      <section className="space-y-6 md:space-y-8" aria-label="Analytics and trends">
        {/* Row 1: Population Pyramid + Nationality Mix + Race Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
          {/* Population Pyramid — Staff (left) vs Worker (right) by Age Group */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 sm:h-[550px] flex flex-col lg:col-span-2 overflow-hidden">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Users2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" aria-hidden="true" />
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 min-w-0">Workforce Age Pyramid</h3>
                </div>
                <p className="text-sm text-gray-600">Staff (left) vs Worker (right) by age group</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-600">
                  {workforceAgeProfile?.overall?.median_age || workforceAgeProfile?.overall?.average_age?.toFixed(1) || stats.totalEmployees || 'N/A'}
                </p>
                <p className="text-xs text-gray-500">
                  {workforceAgeProfile?.overall?.median_age ? 'Median Age' : workforceAgeProfile?.overall?.average_age ? 'Avg Age' : 'Total'}
                </p>
              </div>
            </div>

            {/* Summary badges — side by side at every breakpoint (grid keeps
                them on one row on mobile instead of flex-wrap stacking them). */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3">
              {workforceAgeProfile?.staff?.count > 0 && (
                <div className="flex items-center gap-1.5 sm:gap-2 bg-blue-50 border border-blue-200 rounded-lg px-2.5 sm:px-3 py-1.5 min-w-0">
                  <Briefcase className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" aria-hidden="true" />
                  <span className="text-xs font-semibold text-blue-800 truncate">
                    Staff: {workforceAgeProfile.staff.count} · Median {workforceAgeProfile.staff.median_age || workforceAgeProfile.staff.average_age?.toFixed(0) || '—'}
                  </span>
                </div>
              )}
              {workforceAgeProfile?.worker?.count > 0 && (
                <div className="flex items-center gap-1.5 sm:gap-2 bg-amber-50 border border-amber-200 rounded-lg px-2.5 sm:px-3 py-1.5 min-w-0">
                  <HardHat className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" aria-hidden="true" />
                  <span className="text-xs font-semibold text-amber-800 truncate">
                    Worker: {workforceAgeProfile.worker.count} · Median {workforceAgeProfile.worker.median_age || workforceAgeProfile.worker.average_age?.toFixed(0) || '—'}
                  </span>
                </div>
              )}
            </div>

            <div className="flex-1 min-h-0">
              {(() => {
                const staffDist = workforceAgeProfile?.staff?.distribution || [];
                const workerDist = workforceAgeProfile?.worker?.distribution || [];
                const ageDist = ageGroupData || [];
                const hasClassification = staffDist.length > 0 || workerDist.length > 0;

                if (!hasClassification && ageDist.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center h-full">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <Users className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500">No age data available</p>
                      <p className="text-xs text-gray-400 mt-1">Employee date of birth data needed</p>
                    </div>
                  );
                }

                if (hasClassification) {
                  const allGroups = new Set([...staffDist.map(d => d.age_group), ...workerDist.map(d => d.age_group)]);
                  const sortedGroups = Array.from(allGroups).sort((a, b) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0));
                  const staffMap = Object.fromEntries(staffDist.map(d => [d.age_group, d]));
                  const workerMap = Object.fromEntries(workerDist.map(d => [d.age_group, d]));
                  const maxCount = Math.max(
                    ...staffDist.map(d => d.count || 0),
                    ...workerDist.map(d => d.count || 0),
                    1
                  );
                  const pyramidData = sortedGroups.map(group => ({
                    ageGroup: `${group}`,
                    staffNeg: -(staffMap[group]?.count || 0),
                    worker: workerMap[group]?.count || 0,
                    staffCount: staffMap[group]?.count || 0,
                    workerCount: workerMap[group]?.count || 0,
                    staffPct: staffMap[group]?.percentage || 0,
                    workerPct: workerMap[group]?.percentage || 0,
                  }));
                  return (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={pyramidData} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }} stackOffset="sign">
                        <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_COLOR} horizontal={false} />
                        <XAxis
                          type="number"
                          domain={[-maxCount, maxCount]}
                          tick={{ fontSize: 11, fill: CHART_AXIS_COLOR }}
                          tickFormatter={(v) => Math.abs(v)}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          type="category"
                          dataKey="ageGroup"
                          width={50}
                          tick={{ fontSize: 11, fill: CHART_AXIS_COLOR, fontWeight: 600 }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(v) => `${v}`}
                        />
                        <ReferenceLine x={0} stroke={CHART_AXIS_COLOR} strokeWidth={1} />
                        <Tooltip
                          contentStyle={CHART_TOOLTIP_STYLE}
                          formatter={(value, name, props) => {
                            const isStaff = name === 'staffNeg';
                            const count = isStaff ? props.payload.staffCount : props.payload.workerCount;
                            const pct = isStaff ? props.payload.staffPct : props.payload.workerPct;
                            return [`${count} (${pct?.toFixed(1)}%)`, isStaff ? 'Staff' : 'Worker'];
                          }}
                          labelFormatter={(label) => `${label} years`}
                        />
                        <Legend
                          verticalAlign="top"
                          height={24}
                          formatter={(value) => (
                            <span className="text-xs font-medium text-gray-700">
                              {value === 'staffNeg' ? '← Staff' : 'Worker →'}
                            </span>
                          )}
                        />
                        <Bar dataKey="staffNeg" name="staffNeg" fill="#3B82F6" radius={[4, 0, 0, 4]} barSize={18} />
                        <Bar dataKey="worker" name="worker" fill="#F59E0B" radius={[0, 4, 4, 0]} barSize={18} />
                      </BarChart>
                    </ResponsiveContainer>
                  );
                }

                // Fallback: only overall age data, no staff/worker split
                return (
                  <div className="overflow-y-auto space-y-3 h-full">
                    {ageDist.map((group, index) => {
                      const color = AGE_GROUP_COLORS[index % AGE_GROUP_COLORS.length];
                      const total = ageDist.reduce((sum, g) => sum + g.count, 0);
                      const pct = group.percentage || (total > 0 ? (group.count / total) * 100 : 0);
                      return (
                        <div key={group.age_group} className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">{group.age_group} years</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-semibold text-gray-900">{group.count}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${color.light} ${color.text}`}>{pct.toFixed(1)}%</span>
                            </div>
                          </div>
                          <div className="bg-gray-200 rounded-full h-2.5">
                            <div className={`h-2.5 rounded-full ${color.bg} transition-all duration-500`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Nationality Mix - Horizontal Bar Chart */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 sm:h-[550px] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 flex-shrink-0" aria-hidden="true" />
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 min-w-0">Nationality Mix</h3>
                </div>
                <p className="text-sm text-gray-600">Employee nationality breakdown</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-indigo-600">
                  {stats.totalEmployees || nationalityData.reduce((sum, nation) => sum + nation.count, 0)}
                </p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
            {nationalityData.length > 0 ? (
              <div className="flex flex-col h-full">
                <ResponsiveContainer width="100%" height={Math.max(200, nationalityData.length * 36)}>
                  <BarChart data={nationalityData} layout="vertical" margin={{ top: 0, right: 40, left: 0, bottom: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis 
                      type="category" 
                      dataKey="nationality" 
                      width={80} 
                      tick={{ fontSize: 12, fill: '#374151' }} 
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(value, name, props) => [`${value} (${props.payload.percentage?.toFixed(1)}%)`, 'Count']}
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px' }}
                    />
                    <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={20}>
                      {nationalityData.map((entry, index) => {
                        return <Cell key={`nat-${index}`} fill={CHART_COLORS_6[index % CHART_COLORS_6.length]} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">No nationality data available</p>
                <p className="text-xs text-gray-400 mt-1">Employee nationality data needed</p>
              </div>
            )}
            </div>
          </div>

          {/* Race Distribution - Actual Pie Chart */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 sm:h-[550px] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Users2 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" aria-hidden="true" />
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 min-w-0">Race Distribution</h3>
                </div>
                <p className="text-xs sm:text-sm text-gray-600">For MOM statistical reporting</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-purple-600">
                  {raceData.reduce((sum, race) => sum + race.count, 0)}
                </p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
            
            <div className="flex-1 flex flex-col">
            {raceData.length > 0 ? (
              <div className="flex flex-col flex-1 w-full min-w-0">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={raceData.map((r, i) => ({ ...r, name: r.race, fill: CHART_COLORS_6[i % CHART_COLORS_6.length] }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      dataKey="count"
                      paddingAngle={2}
                    >
                      {raceData.map((_, index) => (
                        <Cell key={`race-${index}`} fill={CHART_COLORS_6[index % CHART_COLORS_6.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [value, name]}
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 w-full mt-2">
                  {raceData.map((race, index) => {
                    const colors = ['#3B82F6', '#EF4444', '#F97316', '#10B981', '#8B5CF6', '#6366F1'];
                    return (
                      <div key={race.race} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors[index % 6] }}></div>
                          <span className="text-sm font-medium text-gray-700">{race.race}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-semibold text-gray-900">{race.count}</span>
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                            {race.percentage?.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users2 className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">No race data available</p>
                <p className="text-xs text-gray-400 mt-1">Race data needed for MOM reporting</p>
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Row 2: Gender Distribution + Tenure Distribution + Department Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">

          {/* Gender Distribution - Recharts Donut */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 sm:h-[600px] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-pink-600 flex-shrink-0" aria-hidden="true" />
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 min-w-0">Gender Distribution</h3>
                </div>
                <p className="text-sm text-gray-600">Workforce gender breakdown</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-pink-600">
                  {genderData.reduce((sum, gender) => sum + gender.count, 0)}
                </p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
            
            <div className="flex-1 flex flex-col">
            {genderData.length > 0 ? (
              <div className="flex flex-col flex-1 w-full min-w-0">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={genderData.map((g, i) => ({ ...g, name: g.gender, fill: CHART_COLORS_5[i % CHART_COLORS_5.length] }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      dataKey="count"
                      paddingAngle={3}
                    >
                      {genderData.map((_, index) => (
                        <Cell key={`gender-${index}`} fill={CHART_COLORS_5[index % CHART_COLORS_5.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [value, name]}
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-3 w-full mt-2">
                  {genderData.map((gender, index) => {
                    const colors = CHART_COLORS_5;
                    return (
                      <div key={gender.gender} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors[index % 5] }}></div>
                          <span className="text-sm font-medium text-gray-700">{gender.gender}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-semibold text-gray-900">{gender.count}</span>
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                            {gender.percentage?.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">No gender data available</p>
                <p className="text-xs text-gray-400 mt-1">Employee gender data needed</p>
              </div>
            )}
            </div>
          </div>
          {/* Tenure Distribution - Bar Chart */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 sm:h-[600px] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" aria-hidden="true" />
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 min-w-0">Tenure Distribution</h3>
                </div>
                <p className="text-sm text-gray-600">Bar chart visualization</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-purple-600">
                  {stats.totalEmployees || tenureData.reduce((sum, tenure) => sum + tenure.count, 0)}
                </p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-4">
              {tenureData.map((tenure, index) => {
                const color = TENURE_COLORS[index % TENURE_COLORS.length];
                
                return (
                  <div key={tenure.tenure_range} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">{tenure.tenure_range}</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-semibold text-gray-900">{tenure.count}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${color.light} ${color.text}`}>
                          {tenure.percentage?.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1">
                        <div className="bg-gray-200 rounded-full h-3">
                          <div 
                            className={`h-3 rounded-full ${color.bg} transition-all duration-500`}
                            style={{ width: `${tenure.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className={`w-8 h-8 rounded-full ${color.bg} flex items-center justify-center`}>
                        <Clock className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {tenureData.length === 0 && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">No tenure data available</p>
                <p className="text-xs text-gray-400 mt-1">Employee start date data needed</p>
              </div>
            )}
          </div>

          {/* Department Analytics - Real Implementation */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 sm:h-[600px] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 flex-shrink-0" aria-hidden="true" />
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 min-w-0">Department Analytics</h3>
                </div>
                <p className="text-sm text-gray-600">Size, age & tenure analysis</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-emerald-600">
                  {departmentData.length || stats.departments || 0}
                </p>
                <p className="text-xs text-gray-500">Departments</p>
                {(departmentData.length || stats.departments || 0) > 5 && (
                  <button
                    onClick={() => setShowDepartmentModal(true)}
                    className="mt-2 text-xs text-emerald-600 hover:text-emerald-700 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 rounded"
                    aria-label="View all department analytics"
                  >
                    View All
                  </button>
                )}
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
            {departmentData.length > 0 ? (
              <div className="space-y-4">
                {departmentData.map((dept, index) => {
                  const color = getDepartmentDashboardColor(dept.department_name);
                  const DeptIcon = getDepartmentIcon(dept.department_name);
                  
                  return (
                    <div key={dept.department_name} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${color.bg}`}></div>
                          <span className="text-sm font-medium text-gray-700">{dept.department_name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-semibold text-gray-900">{dept.employee_count}</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${color.light} ${color.text}`}>
                            {dept.percentage}%
                          </span>
                        </div>
                      </div>
                      
                      {/* Department metrics */}
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <p className="font-semibold text-gray-900">{dept.employee_count}</p>
                          <p className="text-gray-500">Size</p>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <p className="font-semibold text-gray-900">
                            {dept.average_age ? `${dept.average_age}y` : 'N/A'}
                          </p>
                          <p className="text-gray-500">Avg Age</p>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <p className="font-semibold text-gray-900">
                            {dept.average_tenure ? `${dept.average_tenure}y` : 'N/A'}
                          </p>
                          <p className="text-gray-500">Avg Tenure</p>
                        </div>
                      </div>
                      
                      {/* Progress bar for department size */}
                      <div className="flex items-center space-x-2">
                        <div className="flex-1">
                          <div className="bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${color.bg} transition-all duration-500`}
                              style={{ width: `${dept.percentage}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className={`w-6 h-6 rounded-full ${color.bg} flex items-center justify-center`}>
                          <DeptIcon className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-gray-400" />
                </div>
                {stats.departments > 0 ? (
                  <>
                    <p className="text-sm text-gray-500">Department analytics temporarily unavailable</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {stats.departments} departments found, but detailed analytics are not accessible
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-500">No department data available</p>
                    <p className="text-xs text-gray-400 mt-1">Employee department assignments needed</p>
                  </>
                )}
              </div>
            )}
            </div>
          </div>
        </div>
      </section>

      {/* Department Analytics Modal */}
      {showDepartmentModal && (
        <Modal
          isOpen
          onClose={() => setShowDepartmentModal(false)}
          size="xl"
          theme="admin"
          className="max-w-4xl"
          customHeader={(
            <div className="modal-header">
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-2xl font-bold text-gray-900 flex items-center gap-2 min-w-0">
                  <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 flex-shrink-0" />
                  <span className="truncate">Department Analytics</span>
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">Complete department breakdown with size, age & tenure analysis</p>
              </div>
              <button
                onClick={() => setShowDepartmentModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                aria-label="Close modal"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          )}
          footer={(
            <div className="flex items-center justify-between w-full">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{departmentData.length}</span> departments • 
                <span className="font-medium ml-1">{departmentData.reduce((sum, dept) => sum + dept.employee_count, 0)}</span> total employees
              </div>
              <button
                onClick={() => setShowDepartmentModal(false)}
                className="btn btn-admin-primary"
              >
                Close
              </button>
            </div>
          )}
        >
              {departmentData.length > 0 ? (
                <>
                  {/* Mobile: stacked cards (DS — modal tables need a card fallback below md) */}
                  <div className="md:hidden space-y-3">
                    {departmentData.map((dept) => {
                      const color = getDepartmentDashboardColor(dept.department_name);
                      return (
                        <div key={dept.department_name} className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className={`w-3 h-3 rounded-full ${color.bg} flex-shrink-0`}></div>
                              <span className="font-medium text-gray-900 truncate">{dept.department_name}</span>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${color.light} ${color.text} flex-shrink-0`}>
                              {dept.percentage}%
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                              <p className="text-xs text-gray-500">Size</p>
                              <p className="text-sm font-semibold text-gray-900">{dept.employee_count}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Avg Age</p>
                              <p className="text-sm font-medium text-gray-900">{dept.average_age ? `${dept.average_age}y` : 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Avg Tenure</p>
                              <p className="text-sm font-medium text-gray-900">{dept.average_tenure ? `${dept.average_tenure}y` : 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop: table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-900">Department</th>
                          <th className="text-center py-3 px-4 font-semibold text-gray-900">Size</th>
                          <th className="text-center py-3 px-4 font-semibold text-gray-900">% of Total</th>
                          <th className="text-center py-3 px-4 font-semibold text-gray-900">Avg Age</th>
                          <th className="text-center py-3 px-4 font-semibold text-gray-900">Avg Tenure</th>

                        </tr>
                      </thead>
                      <tbody>
                        {departmentData.map((dept, index) => {
                          const color = getDepartmentDashboardColor(dept.department_name);

                          return (
                            <tr key={dept.department_name} className={`border-b border-gray-100 hover:${color.light} transition-colors`}>
                              <td className="py-4 px-4">
                                <div className="flex items-center space-x-3">
                                  <div className={`w-3 h-3 rounded-full ${color.bg}`}></div>
                                  <span className="font-medium text-gray-900">{dept.department_name}</span>
                                </div>
                              </td>
                              <td className="py-4 px-4 text-center">
                                <span className="font-semibold text-gray-900">{dept.employee_count}</span>
                              </td>
                              <td className="py-4 px-4 text-center">
                                <div className="flex items-center justify-center space-x-2">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${color.light} ${color.text}`}>
                                    {dept.percentage}%
                                  </span>
                                </div>
                              </td>
                              <td className="py-4 px-4 text-center">
                                <span className="font-medium text-gray-900">
                                  {dept.average_age ? `${dept.average_age} years` : 'N/A'}
                                </span>
                              </td>
                              <td className="py-4 px-4 text-center">
                                <span className="font-medium text-gray-900">
                                  {dept.average_tenure ? `${dept.average_tenure} years` : 'N/A'}
                                </span>
                              </td>

                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg text-gray-500">No department data available</p>
                  <p className="text-sm text-gray-400 mt-2">Employee department assignments needed</p>
                </div>
              )}
        </Modal>
      )}
    </>
  );
};

export default WorkforceAnalyticsSection;
